import { ApiConsumer } from 'api/ApiConsumer';
import { PageTitleContext } from 'context/PageTitleContext';
import { useContext, useEffect, useState, useRef, useCallback } from 'react';
import { CrumbsContext } from 'context/CrumbsContext';
import { API_ROUTES } from 'routes/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57'];

const SUGGESTED_PROMPTS = [
  { label: 'Top 10 most played songs this week', icon: '🎵' },
  { label: 'Local vs international content share by station this month', icon: '🌍' },
  { label: 'Top artists this month', icon: '🎤' },
  { label: 'Which stations have the most unrecognised plays this week?', icon: '❓' },
  { label: 'Play count trends over the last 7 days', icon: '📈' },
  { label: 'Top 5 most played songs on Yarona FM this month', icon: '📻' },
];

export default function Chat() {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const { setTitle } = useContext(PageTitleContext);
  const { setCrumbs } = useContext(CrumbsContext);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [expandedTables, setExpandedTables] = useState({});

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    setTitle('AI Insights');
    setCrumbs([
      { name: 'Monitoring', link: '/monitoring' },
      { name: 'AI Insights', link: '/reports/insights' },
    ]);

    setMessages([
      {
        id: 1,
        text: 'Hello! I can help you explore your broadcast monitoring data. Ask me anything about play counts, airtime, stations, artists, and more.',
        sender: 'bot',
        timestamp: new Date().toISOString(),
        showSuggestions: true,
      }
    ]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendMessage = async (text) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      text,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => prev.map(m => ({ ...m, showSuggestions: false })).concat(userMessage));
    setMessage('');
    setLoading(true);

    try {
      const response = await ApiConsumer.post(API_ROUTES.AI.INSIGHT, { message: text });
      const data = response.data;

      const botMessage = {
        id: Date.now() + 1,
        sender: 'bot',
        timestamp: new Date().toISOString(),
        text: data.message || null,
        chartData: data.type === 'chart' ? data.data : null,
        chartType: data.type === 'chart' ? data.chartType : null,
        chartTitle: data.type === 'chart' ? (data.chartTitle || data.message || 'Chart') : null,
        rows: data.rows || null,
        totalRows: data.totalRows || null,
        truncated: data.truncated || false,
        sql: data.sql || null,
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        text: 'Sorry, there was an error processing your request. Please try again.',
        sender: 'bot',
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    sendMessage(message);
  };

  const handleSuggestedPrompt = (prompt) => {
    sendMessage(prompt);
  };

  const toggleTableExpand = (msgId) => {
    setExpandedTables(prev => ({ ...prev, [msgId]: !prev[msgId] }));
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '-';
    const num = Number(seconds);
    if (isNaN(num)) return seconds;
    const mins = Math.floor(num / 60);
    const secs = Math.round(num % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCellValue = (key, value) => {
    if (value === null || value === undefined) return '-';
    if (key.toLowerCase().includes('duration') || key.toLowerCase().includes('airtime')) {
      const num = Number(value);
      if (!isNaN(num) && num > 60) return formatDuration(num);
    }
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return String(value);
  };

  const formatColumnHeader = (key) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const exportToCsv = (rows) => {
    if (!rows || rows.length === 0) return;
    const columns = Object.keys(rows[0]);

    // Build CSV with proper escaping
    const escapeCsvValue = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const header = columns.map(col => escapeCsvValue(formatColumnHeader(col))).join(',');
    const csvRows = rows.map(row =>
      columns.map(col => escapeCsvValue(row[col])).join(',')
    );
    const csvContent = [header, ...csvRows].join('\n');

    // Trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ai-insights-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Determine if a message has rich content (chart or data table) that needs full width
  const hasRichContent = (msg) => {
    return (msg.chartData && msg.chartType) || (msg.rows && msg.rows.length > 0);
  };

  const renderSuggestedPrompts = () => (
    <div className="mt-4">
      <p className="text-sm text-gray-500 mb-3">Try asking:</p>
      <div className="flex flex-wrap gap-2">
        {SUGGESTED_PROMPTS.map((prompt, index) => (
          <button
            key={index}
            onClick={() => handleSuggestedPrompt(prompt.label)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 rounded-full hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 transition-all duration-200 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            <span>{prompt.icon}</span>
            <span>{prompt.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderDataTable = (msg) => {
    const rows = msg.rows;
    if (!rows || rows.length === 0) return null;

    const columns = Object.keys(rows[0]);
    const isExpanded = expandedTables[msg.id];
    const PREVIEW_COUNT = 5;
    const displayRows = isExpanded ? rows : rows.slice(0, PREVIEW_COUNT);
    const hasMore = rows.length > PREVIEW_COUNT;

    return (
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
            Data ({msg.totalRows || rows.length} {msg.totalRows === 1 || rows.length === 1 ? 'row' : 'rows'})
            {msg.truncated && <span className="text-xs text-gray-400 ml-1">(truncated)</span>}
          </h4>
          <button
            onClick={() => exportToCsv(rows)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-100 hover:text-gray-800 transition-colors"
            title="Download as CSV"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
        </div>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b">#</th>
                {columns.map(col => (
                  <th key={col} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b whitespace-nowrap">
                    {formatColumnHeader(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-2 text-gray-400 text-xs">{i + 1}</td>
                  {columns.map(col => (
                    <td key={col} className="px-3 py-2 text-gray-800 whitespace-nowrap">
                      {formatCellValue(col, row[col])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <button
            onClick={() => toggleTableExpand(msg.id)}
            className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors"
          >
            {isExpanded ? 'Show less' : `Show all ${rows.length} rows`}
          </button>
        )}
      </div>
    );
  };

  const renderChart = (chartData, chartType, title) => {
    if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {
      return <div className="text-red-500 text-sm">No chart data available</div>;
    }

    const dataKeys = Object.keys(chartData[0]).filter(key => key !== 'name');
    const chartHeight = 350;

    switch (chartType.toLowerCase()) {
      case 'bar':
        return (
          <div className="bg-white rounded-lg">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={80}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}
                />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                {dataKeys.map((key, index) => (
                  <Bar key={key} dataKey={key} fill={COLORS[index % COLORS.length]} radius={[4, 4, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        );

      case 'pie':
        return (
          <div className="bg-white rounded-lg">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={120}
                  fill="#8884d8"
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );

      case 'line':
        return (
          <div className="bg-white rounded-lg">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={80}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                {dataKeys.map((key, index) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={COLORS[index % COLORS.length]}
                    strokeWidth={2}
                    activeDot={{ r: 6 }}
                    dot={{ r: 3 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        );

      case 'area':
        return (
          <div className="bg-white rounded-lg">
            <ResponsiveContainer width="100%" height={chartHeight}>
              <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                  height={80}
                />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }} />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                {dataKeys.map((key, index) => (
                  <Area
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={COLORS[index % COLORS.length]}
                    fill={COLORS[index % COLORS.length]}
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return <div className="text-gray-500 text-sm">Unsupported chart type: {chartType}</div>;
    }
  };

  const renderMessage = (msg) => {
    const isUser = msg.sender === 'user';
    const rich = hasRichContent(msg);

    if (isUser) {
      return (
        <div key={msg.id} className="flex justify-end">
          <div className="max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-4 py-2 bg-blue-500 text-white rounded-br-none">
            <p>{msg.text}</p>
            <div className="text-xs mt-1 opacity-70">
              {new Date(msg.timestamp).toLocaleTimeString()}
            </div>
          </div>
        </div>
      );
    }

    // Bot message with rich content — single unified card
    if (rich) {
      return (
        <div key={msg.id} className="flex justify-start">
          <div className="w-full max-w-4xl bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            {/* Text summary */}
            {msg.text && (
              <div className="px-5 pt-4 pb-3">
                <div className="text-sm text-gray-700 leading-relaxed space-y-1">
                  {msg.text.split('\n').map((line, i) => (
                    <p key={i}>{line}</p>
                  ))}
                </div>
                <div className="text-xs mt-2 text-gray-400">
                  {new Date(msg.timestamp).toLocaleTimeString()}
                </div>
              </div>
            )}

            {/* Chart */}
            {msg.chartData && msg.chartType && (
              <div className="px-4 py-4 border-t border-gray-100">
                {renderChart(msg.chartData, msg.chartType, msg.chartTitle)}
              </div>
            )}

            {/* Data table */}
            {msg.rows && msg.rows.length > 0 && (
              <div className="px-5 pb-4 border-t border-gray-100 pt-4">
                {renderDataTable(msg)}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Bot message — plain text only
    return (
      <div key={msg.id} className="flex justify-start">
        <div className="max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-4 py-2 bg-gray-200 text-gray-800 rounded-bl-none">
          {msg.text && msg.text.split('\n').map((line, i) => (
            <p key={i} className="leading-relaxed">{line}</p>
          ))}
          <div className="text-xs mt-1 opacity-70">
            {new Date(msg.timestamp).toLocaleTimeString()}
          </div>
          {msg.showSuggestions && renderSuggestedPrompts()}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Messages area */}
      <div className="p-4 pb-20">
        <div className="space-y-4 max-w-4xl mx-auto">
          {messages.map(renderMessage)}

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-gray-200 rounded-lg px-4 py-3 rounded-bl-none">
                <div className="flex items-center gap-2 text-gray-500">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <span className="text-sm">Analysing your data...</span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area — fixed to the bottom, offset for sidebar */}
      <div className="fixed bottom-0 right-0 left-0 lg:left-64 border-t border-gray-200 bg-white px-4 py-3 z-10">
        <form onSubmit={handleSendMessage} className="flex items-center max-w-4xl mx-auto gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Ask about play counts, airtime, stations, artists..."
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            disabled={loading}
          />
          <button
            type="submit"
            className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg transition duration-200 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || !message.trim()}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Analysing...
              </span>
            ) : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}
